// ARCHIVO: src/pages/Dashboard.jsx
// UBICACIÓN: C:\xampp\htdocs\react-migration\src\pages\Dashboard.jsx
// DESCRIPCIÓN: Dashboard principal con estadísticas del comercio actual

import React, { useState, useEffect } from 'react';
import { TrendingUp, TrendingDown, Package, DollarSign, Users, ShoppingCart, Warehouse, Calculator, AlertCircle, CreditCard, Wallet, Clock } from 'lucide-react';
import { getDashboardStats, getComercioData } from '../api/database.js';
import { getComercioNombre } from '../config/comercio.js';
import { inicializarComercio } from '../utils/comercioInit.js';
import { API_BASE_URL } from '../config/comercio.js';
import ComercioStatus from '../components/ComercioStatus.jsx';

// Función para formatear valores monetarios
const formatearMoneda = (valor) => {
  if (!valor || valor === 0) return '$ 0,00';
  
  return new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(valor);
};

const Dashboard = () => {
  // El manejo de token se centraliza en App.jsx

  const [stats, setStats] = useState({
    totalProductos: 0,
    totalStock: 0,
    ventasHoy: 0,
    productosBajoStock: 0,
    valorTotalInventario: 0,
    valorTotalCompra: 0
  });
  const [ventasCaja, setVentasCaja] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [comercioInfo, setComercioInfo] = useState(null);

  useEffect(() => {
    // Debug del token
    // Debug removido - token manejado centralmente
    
    const cargarDatos = async () => {
      try {
        // Forzar inicialización del comercio primero
        const initResult = await inicializarComercio();
        console.log('🚀 [DASHBOARD] Inicialización forzada:', initResult);
        
        if (!initResult.success) {
          throw new Error(initResult.error || 'Error inicializando comercio');
        }
        
        setLoading(true);
        setError(null);
        
        // Cargar estadísticas del dashboard
        const statsResponse = await getDashboardStats();
        
        if (statsResponse.success) {
          setStats(statsResponse.stats);
        } else {
          console.warn('⚠️ [DASHBOARD] Error en estadísticas:', statsResponse.error);
          // Usar estadísticas por defecto
          setStats({
            totalProductos: 0,
            totalStock: 0,
            ventasHoy: 0,
            productosBajoStock: 0,
            valorTotalInventario: 0,
            valorTotalCompra: 0
          });
        }
        
        // Cargar información del comercio
        console.log('🔍 [DASHBOARD] Llamando getComercioData...');
        const comercioResponse = await getComercioData();
        console.log('🔍 [DASHBOARD] Respuesta getComercioData:', comercioResponse);
        
        if (comercioResponse.success) {
          console.log('🔍 [DASHBOARD] Estableciendo comercioInfo:', comercioResponse.data);
          setComercioInfo(comercioResponse.data);
        } else {
          console.warn('⚠️ [DASHBOARD] Error en datos del comercio:', comercioResponse.error);
          // Setear comercio por defecto para evitar loading infinito
          setComercioInfo({
            fantasia: 'Comercio',
            descripcion: 'Sistema de Ventas SinFilas',
            logo_path: null
          });
        }
        
        // Cargar ventas del día y estado de caja
        try {
          const token = localStorage.getItem('comercio_token');
          console.log('🔑 [DASHBOARD] Token encontrado:', token ? 'SÍ' : 'NO');
          
          if (token) {
            const url = `${API_BASE_URL}/dashboard_ventas_caja.php?token=${token}`;
            console.log('🌐 [DASHBOARD] Llamando a API:', url);
            
            const ventasResponse = await fetch(url);
            console.log('📡 [DASHBOARD] Response status:', ventasResponse.status);
            
            const ventasData = await ventasResponse.json();
            console.log('📦 [DASHBOARD] Datos recibidos:', ventasData);
            
            if (ventasData.success) {
              setVentasCaja(ventasData);
              console.log('✅ [DASHBOARD] Datos de ventas y caja cargados correctamente');
            } else {
              console.error('❌ [DASHBOARD] Error en ventas/caja:', ventasData.error);
            }
          } else {
            console.warn('⚠️ [DASHBOARD] No hay token disponible');
          }
        } catch (ventasErr) {
          console.error('💥 [DASHBOARD] Exception al cargar ventas/caja:', ventasErr);
        }
        
      } catch (err) {
        console.error('❌ [DASHBOARD] Error:', err);
        setError(`Error al cargar datos del dashboard: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    cargarDatos();
  }, []);

  console.log('🔍 [DASHBOARD] Estado actual - loading:', loading, 'comercioInfo:', comercioInfo);
  
  if (loading || !comercioInfo) {
    console.log('🔍 [DASHBOARD] Mostrando loading...');
    return (
      <div className="dashboard-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Cargando datos del comercio...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-page">
        <div className="error-container">
          <h2>Error al cargar datos</h2>
          <p>{error}</p>
          <button onClick={() => window.location.reload()}>Reintentar</button>
        </div>
      </div>
    );
  }


  return (
    <div className="dashboard-page">
      {/* Header del Dashboard */}
      <div className="header">
        <div className="header-content">
          {comercioInfo?.logo_path && (
            <img 
              src={`https://www.sinfilas.com.ar/sf3/ventas/images/comercios/${comercioInfo.id}/${comercioInfo.logo_path ? comercioInfo.logo_path.split('/').pop() : ''}`}
              alt="Logo del comercio"
              className="comercio-logo-header"
              onError={(e) => e.target.style.display = 'none'}
            />
          )}
          <div className="header-text">
            <h1>¡Bienvenido al Sistema SinFilas!</h1>
            <p>Panel de control del comercio: <strong>{comercioInfo?.fantasia || 'Comercio'}</strong></p>
            {comercioInfo && (
              <p className="comercio-info">{comercioInfo.descripcion}</p>
            )}
          </div>
        </div>
      </div>

      {/* Grid de Estadísticas */}
      <div className="dashboard-grid">
        {/* Total de Productos */}
        <div className="dashboard-card">
          <div className="card-header">
            <div className="card-icon">
              <Calculator size={24} />
            </div>
            <div className="card-content">
              <h3>Total Productos</h3>
              <p>Productos en el sistema</p>
            </div>
          </div>
          <div className="card-value">{stats.totalProductos}</div>
        </div>

        {/* Stock Total */}
        <div className="dashboard-card">
          <div className="card-header">
            <div className="card-icon">
              <Warehouse size={24} />
            </div>
            <div className="card-content">
              <h3>Stock Total</h3>
              <p>Unidades en inventario</p>
            </div>
          </div>
          <div className="card-value">{stats.totalStock}</div>
        </div>

        {/* Productos con Stock Bajo */}
        <div className="dashboard-card">
          <div className="card-header">
            <div className="card-icon">
              <AlertCircle size={24} />
            </div>
            <div className="card-content">
              <h3>Bajo Stock</h3>
              <p>Productos con stock mínimo</p>
            </div>
          </div>
          <div className="card-value">{stats.productosBajoStock}</div>
        </div>

        {/* Valor Total de Compra */}
        <div className="dashboard-card">
          <div className="card-header">
            <div className="card-icon">
              <TrendingUp size={24} />
            </div>
            <div className="card-content">
              <h3>Valor de Compra</h3>
              <p>Valor del inventario (precio compra)</p>
            </div>
          </div>
          <div className="card-value">{formatearMoneda(stats.valorTotalCompra)}</div>
        </div>

        {/* Valor Total del Inventario (Precio de Venta) */}
        <div className="dashboard-card">
          <div className="card-header">
            <div className="card-icon">
              <DollarSign size={24} />
            </div>
            <div className="card-content">
              <h3>Valor Total</h3>
              <p>Valor del inventario (precio venta)</p>
            </div>
          </div>
          <div className="card-value">{formatearMoneda(stats.valorTotalInventario)}</div>
        </div>

      </div>

      {/* Ventas del Día y Estado de Caja */}
      {ventasCaja && ventasCaja.success && (
        <>
          {/* Sección de Ventas del Día */}
          <div className="ventas-dia-section">
            <div className="section-header">
              <ShoppingCart size={28} />
              <h2>📊 Ventas del Día - {new Date().toLocaleDateString('es-AR')}</h2>
            </div>
            
            <div className="ventas-grid">
              {/* Total de Ventas */}
              <div className="venta-card">
                <div className="venta-icon">
                  <ShoppingCart size={24} />
                </div>
                <div className="venta-info">
                  <h3>Total Ventas</h3>
                  <div className="venta-valor">{ventasCaja.ventas_dia.total_ventas}</div>
                  {ventasCaja.ventas_dia.cambio_vs_ayer.ventas !== 0 && (
                    <div className={`cambio ${ventasCaja.ventas_dia.cambio_vs_ayer.ventas > 0 ? 'positivo' : 'negativo'}`}>
                      {ventasCaja.ventas_dia.cambio_vs_ayer.ventas > 0 ? <TrendingUp size={16} /> : <TrendingDown size={16} />}
                      {Math.abs(ventasCaja.ventas_dia.cambio_vs_ayer.ventas).toFixed(1)}% vs ayer
                    </div>
                  )}
                </div>
              </div>

              {/* Ingresos Totales */}
              <div className="venta-card destacado">
                <div className="venta-icon">
                  <DollarSign size={24} />
                </div>
                <div className="venta-info">
                  <h3>Ingresos del Día</h3>
                  <div className="venta-valor">{formatearMoneda(ventasCaja.ventas_dia.ingresos_totales)}</div>
                  {ventasCaja.ventas_dia.cambio_vs_ayer.ingresos !== 0 && (
                    <div className={`cambio ${ventasCaja.ventas_dia.cambio_vs_ayer.ingresos > 0 ? 'positivo' : 'negativo'}`}>
                      {ventasCaja.ventas_dia.cambio_vs_ayer.ingresos > 0 ? <TrendingUp size={16} /> : <TrendingDown size={16} />}
                      {Math.abs(ventasCaja.ventas_dia.cambio_vs_ayer.ingresos).toFixed(1)}% vs ayer
                    </div>
                  )}
                </div>
              </div>

              {/* Ticket Promedio */}
              <div className="venta-card">
                <div className="venta-icon">
                  <Calculator size={24} />
                </div>
                <div className="venta-info">
                  <h3>Ticket Promedio</h3>
                  <div className="venta-valor">{formatearMoneda(ventasCaja.ventas_dia.ticket_promedio)}</div>
                </div>
              </div>

              {/* Productos Vendidos */}
              <div className="venta-card">
                <div className="venta-icon">
                  <Package size={24} />
                </div>
                <div className="venta-info">
                  <h3>Productos Vendidos</h3>
                  <div className="venta-valor">{ventasCaja.ventas_dia.total_productos_vendidos}</div>
                </div>
              </div>
            </div>

            {/* Top Productos del Día */}
            {ventasCaja.top_productos && ventasCaja.top_productos.length > 0 && (
              <div className="top-productos">
                <h3>🏆 Productos Más Vendidos Hoy</h3>
                <div className="productos-lista">
                  {ventasCaja.top_productos.map((producto, index) => (
                    <div key={index} className="producto-item">
                      <div className="producto-rank">#{index + 1}</div>
                      <div className="producto-nombre">{producto.nombre}</div>
                      <div className="producto-cantidad">{producto.cantidad_vendida} unidades</div>
                      <div className="producto-ingresos">{formatearMoneda(producto.ingresos_producto)}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Estado de Caja */}
          <div className="caja-section">
            <div className="section-header">
              <Wallet size={28} />
              <h2>💰 Estado de Caja</h2>
            </div>
            
            <div className="caja-grid">
              {/* Efectivo */}
              <div className="metodo-pago-card">
                <div className="metodo-icon efectivo">
                  <DollarSign size={28} />
                </div>
                <h3>Efectivo</h3>
                <div className="metodo-monto">{formatearMoneda(ventasCaja.caja.efectivo)}</div>
              </div>

              {/* Tarjeta */}
              <div className="metodo-pago-card">
                <div className="metodo-icon tarjeta">
                  <CreditCard size={28} />
                </div>
                <h3>Tarjeta</h3>
                <div className="metodo-monto">{formatearMoneda(ventasCaja.caja.tarjeta)}</div>
              </div>

              {/* Transferencia */}
              <div className="metodo-pago-card">
                <div className="metodo-icon transferencia">
                  <TrendingUp size={28} />
                </div>
                <h3>Transferencia</h3>
                <div className="metodo-monto">{formatearMoneda(ventasCaja.caja.transferencia)}</div>
              </div>

              {/* QR */}
              <div className="metodo-pago-card">
                <div className="metodo-icon qr">
                  <Package size={28} />
                </div>
                <h3>QR / Otros</h3>
                <div className="metodo-monto">{formatearMoneda(ventasCaja.caja.qr)}</div>
              </div>
            </div>

            {/* Detalle de métodos de pago */}
            {ventasCaja.caja.metodos_pago && ventasCaja.caja.metodos_pago.length > 0 && (
              <div className="metodos-detalle">
                <h3>Detalle por Método de Pago</h3>
                <table className="tabla-metodos">
                  <thead>
                    <tr>
                      <th>Método</th>
                      <th>Operaciones</th>
                      <th>Monto Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ventasCaja.caja.metodos_pago.map((metodo, index) => (
                      <tr key={index}>
                        <td className="metodo-nombre">{metodo.metodo_pago}</td>
                        <td className="metodo-cantidad">{metodo.cantidad_operaciones}</td>
                        <td className="metodo-total">{formatearMoneda(metodo.monto_total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {/* Estado del Comercio */}
      <div className="comercio-status-section">
        <ComercioStatus />
      </div>

      {/* Mensaje de Bienvenida */}
      <div className="welcome-section">
        <div className="dashboard-title">
          {comercioInfo?.logo_path && (
            <img 
              src={`https://www.sinfilas.com.ar/sf3/ventas/images/comercios/${comercioInfo.id}/${comercioInfo.logo_path ? comercioInfo.logo_path.split('/').pop() : ''}`}
              alt="Logo del comercio"
              className="comercio-logo"
              onError={(e) => e.target.style.display = 'none'}
            />
          )}
          <h2>Sistema SinFilas - Gestión del Comercio {comercioInfo?.fantasia || 'Comercio'}</h2>
        </div>
        <p>Este es el sistema de gestión completo para tu comercio. Utiliza el menú lateral para navegar entre las diferentes secciones.</p>
      </div>

      {/* Estilos CSS */}
      <style jsx>{`
        .dashboard-page {
          padding: 24px;
          background: #f8fafc;
          min-height: 100vh;
        }

        .header {
          text-align: center;
          margin-bottom: 32px;
          padding: 24px;
          background: white;
          border-radius: 16px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
        }

        .header h1 {
          color: #1e293b;
          margin-bottom: 8px;
          font-size: 2.5rem;
          font-weight: 700;
        }

        .header p {
          color: #64748b;
          font-size: 1.1rem;
          margin-bottom: 8px;
        }

        .comercio-info {
          color: #3b82f6;
          font-weight: 500;
          font-style: italic;
        }

        .dashboard-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 24px;
          margin-bottom: 32px;
        }

        .dashboard-card {
          background: white;
          padding: 24px;
          border-radius: 16px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
          transition: transform 0.3s ease, box-shadow 0.3s ease;
        }

        .dashboard-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 8px 30px rgba(0, 0, 0, 0.15);
        }

        .card-header {
          display: flex;
          align-items: center;
          gap: 16px;
          margin-bottom: 16px;
        }

        .card-icon {
          width: 48px;
          height: 48px;
          background: linear-gradient(135deg, #3b82f6, #1d4ed8);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
        }

        .card-content h3 {
          color: #1e293b;
          margin: 0 0 4px 0;
          font-size: 1.1rem;
          font-weight: 600;
        }

        .card-content p {
          color: #64748b;
          margin: 0;
          font-size: 0.9rem;
        }

        .card-value {
          font-size: 2rem;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 16px;
          word-break: break-word;
          overflow-wrap: break-word;
          line-height: 1.2;
        }

        .card-change {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 0.9rem;
          font-weight: 500;
        }

        .card-change.positive {
          color: #059669;
        }

        .card-change.negative {
          color: #dc2626;
        }

        .welcome-section {
          background: white;
          padding: 32px;
          border-radius: 16px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
          text-align: center;
        }

        .welcome-section h2 {
          color: #1e293b;
          margin-bottom: 16px;
          font-size: 1.8rem;
          font-weight: 600;
        }

        .welcome-section p {
          color: #64748b;
          margin-bottom: 32px;
          font-size: 1.1rem;
          line-height: 1.6;
        }

        .features-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 24px;
        }

        .feature-item {
          padding: 24px;
          border-radius: 12px;
          background: #f8fafc;
          transition: transform 0.3s ease;
        }

        .feature-item:hover {
          transform: translateY(-4px);
        }

        .feature-item svg {
          color: #3b82f6;
          margin-bottom: 16px;
        }

        .feature-item h3 {
          color: #1e293b;
          margin-bottom: 8px;
          font-size: 1.1rem;
          font-weight: 600;
        }

        .feature-item p {
          color: #64748b;
          font-size: 0.9rem;
          line-height: 1.5;
        }

        .loading-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          height: 50vh;
          gap: 16px;
        }

        .loading-spinner {
          width: 48px;
          height: 48px;
          border: 4px solid #e2e8f0;
          border-top: 4px solid #3b82f6;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .error-container {
          text-align: center;
          padding: 48px;
        }

        .error-container h2 {
          color: #dc2626;
          margin-bottom: 16px;
        }

        .error-container button {
          background: #3b82f6;
          color: white;
          border: none;
          padding: 12px 24px;
          border-radius: 8px;
          cursor: pointer;
          font-size: 1rem;
          transition: background 0.3s ease;
        }

        .error-container button:hover {
          background: #2563eb;
        }

        /* Ventas del Día */
        .ventas-dia-section {
          background: white;
          padding: 32px;
          border-radius: 16px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
          margin-bottom: 24px;
        }

        .section-header {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 24px;
          color: #1e293b;
        }

        .section-header h2 {
          font-size: 1.5rem;
          font-weight: 700;
          margin: 0;
        }

        .ventas-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
          gap: 20px;
          margin-bottom: 32px;
        }

        .venta-card {
          background: linear-gradient(135deg, #f8fafc, #e2e8f0);
          padding: 20px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          gap: 16px;
          transition: transform 0.3s ease, box-shadow 0.3s ease;
        }

        .venta-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
        }

        .venta-card.destacado {
          background: linear-gradient(135deg, #3b82f6, #1d4ed8);
          color: white;
        }

        .venta-card.destacado h3,
        .venta-card.destacado .venta-valor {
          color: white;
        }

        .venta-icon {
          width: 48px;
          height: 48px;
          background: rgba(59, 130, 246, 0.1);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #3b82f6;
        }

        .venta-card.destacado .venta-icon {
          background: rgba(255, 255, 255, 0.2);
          color: white;
        }

        .venta-info {
          flex: 1;
        }

        .venta-info h3 {
          font-size: 0.9rem;
          color: #64748b;
          margin: 0 0 8px 0;
          font-weight: 500;
        }

        .venta-valor {
          font-size: 1.8rem;
          font-weight: 700;
          color: #1e293b;
        }

        .cambio {
          display: flex;
          align-items: center;
          gap: 4px;
          font-size: 0.85rem;
          margin-top: 4px;
          font-weight: 500;
        }

        .cambio.positivo {
          color: #059669;
        }

        .cambio.negativo {
          color: #dc2626;
        }

        /* Top Productos */
        .top-productos {
          margin-top: 24px;
          padding: 20px;
          background: #f8fafc;
          border-radius: 12px;
        }

        .top-productos h3 {
          font-size: 1.2rem;
          color: #1e293b;
          margin-bottom: 16px;
          font-weight: 600;
        }

        .productos-lista {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .producto-item {
          display: flex;
          align-items: center;
          gap: 16px;
          background: white;
          padding: 16px;
          border-radius: 8px;
          transition: transform 0.2s ease;
        }

        .producto-item:hover {
          transform: translateX(4px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }

        .producto-rank {
          width: 32px;
          height: 32px;
          background: linear-gradient(135deg, #3b82f6, #1d4ed8);
          color: white;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          font-size: 0.9rem;
        }

        .producto-nombre {
          flex: 1;
          font-weight: 600;
          color: #1e293b;
        }

        .producto-cantidad {
          color: #64748b;
          font-size: 0.9rem;
        }

        .producto-ingresos {
          color: #059669;
          font-weight: 700;
          font-size: 1rem;
        }

        /* Estado de Caja */
        .caja-section {
          background: white;
          padding: 32px;
          border-radius: 16px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
          margin-bottom: 24px;
        }

        .caja-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 20px;
          margin-bottom: 24px;
        }

        .metodo-pago-card {
          background: #f8fafc;
          padding: 24px;
          border-radius: 12px;
          text-align: center;
          transition: transform 0.3s ease, box-shadow 0.3s ease;
        }

        .metodo-pago-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
        }

        .metodo-icon {
          width: 64px;
          height: 64px;
          margin: 0 auto 16px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
        }

        .metodo-icon.efectivo {
          background: linear-gradient(135deg, #059669, #047857);
        }

        .metodo-icon.tarjeta {
          background: linear-gradient(135deg, #3b82f6, #1d4ed8);
        }

        .metodo-icon.transferencia {
          background: linear-gradient(135deg, #8b5cf6, #6d28d9);
        }

        .metodo-icon.qr {
          background: linear-gradient(135deg, #f59e0b, #d97706);
        }

        .metodo-pago-card h3 {
          font-size: 1rem;
          color: #64748b;
          margin-bottom: 12px;
          font-weight: 500;
        }

        .metodo-monto {
          font-size: 1.5rem;
          font-weight: 700;
          color: #1e293b;
        }

        /* Tabla de Métodos */
        .metodos-detalle {
          margin-top: 24px;
          padding: 20px;
          background: #f8fafc;
          border-radius: 12px;
        }

        .metodos-detalle h3 {
          font-size: 1.1rem;
          color: #1e293b;
          margin-bottom: 16px;
          font-weight: 600;
        }

        .tabla-metodos {
          width: 100%;
          border-collapse: collapse;
        }

        .tabla-metodos th {
          background: #e2e8f0;
          padding: 12px;
          text-align: left;
          font-weight: 600;
          color: #1e293b;
          border-radius: 8px;
        }

        .tabla-metodos td {
          padding: 12px;
          border-bottom: 1px solid #e2e8f0;
        }

        .metodo-nombre {
          font-weight: 600;
          color: #1e293b;
          text-transform: capitalize;
        }

        .metodo-cantidad {
          color: #64748b;
          text-align: center;
        }

        .metodo-total {
          color: #059669;
          font-weight: 700;
          text-align: right;
        }

        /* Responsive */
        @media (max-width: 768px) {
          .dashboard-grid {
            grid-template-columns: 1fr;
          }
          
          .card-value {
            font-size: 1.5rem;
          }
          
          .ventas-grid,
          .caja-grid {
            grid-template-columns: 1fr;
          }

          .producto-item {
            flex-direction: column;
            align-items: flex-start;
            gap: 8px;
          }

          .section-header h2 {
            font-size: 1.2rem;
          }

          .venta-valor {
            font-size: 1.5rem;
          }
        }
        
        @media (min-width: 769px) and (max-width: 1200px) {
          .dashboard-grid {
            grid-template-columns: repeat(2, 1fr);
          }
          
          .card-value {
            font-size: 1.8rem;
          }
        }
      `}</style>
    </div>
  );
};

export default Dashboard;
